// The Vision Board gallery — dream items the player can place on their board.
// Six categories; each item carries a real achievement condition evaluated by the
// engine. No mysticism: you put it up, then you do the work that makes it true.

import type { LifeState, LifeComputed, VisionCategory, VisionCondition } from '@biz-kid/engine';
import { money } from '../ui/kit.js';

export type GalleryItem = {
  key: string;
  category: VisionCategory;
  title: string;
  icon: string;
  condition: VisionCondition;
};

export const CATEGORIES: { key: VisionCategory; label: string; icon: string }[] = [
  { key: 'home', label: 'Dream Home', icon: '🏡' },
  { key: 'business', label: 'Dream Business', icon: '🏪' },
  { key: 'vehicle', label: 'Dream Vehicle', icon: '🚗' },
  { key: 'travel', label: 'Dream Travel', icon: '✈️' },
  { key: 'impact', label: 'Dream Impact', icon: '🤝' },
  { key: 'skill', label: 'Dream Skill', icon: '🧠' },
];

export const GALLERY: GalleryItem[] = [
  // Home
  { key: 'home-first', category: 'home', title: 'A cosy first home', icon: '🏡', condition: { kind: 'netWorth', target: 500 } },
  { key: 'home-garden', category: 'home', title: 'A house with a garden', icon: '🏠', condition: { kind: 'netWorth', target: 2500 } },
  // Business
  { key: 'biz-own', category: 'business', title: 'Own a real business', icon: '🏪', condition: { kind: 'ownAssetType', assetType: 'business' } },
  { key: 'biz-empire', category: 'business', title: 'A business empire', icon: '🏙️', condition: { kind: 'passiveIncome', target: 100 } },
  // Vehicle
  { key: 'veh-bike', category: 'vehicle', title: 'A cool bike', icon: '🚲', condition: { kind: 'netWorth', target: 300 } },
  { key: 'veh-car', category: 'vehicle', title: 'A dream car', icon: '🚗', condition: { kind: 'netWorth', target: 5000 } },
  // Travel
  { key: 'trav-sea', category: 'travel', title: 'A seaside trip', icon: '🏝️', condition: { kind: 'cash', target: 200 } },
  { key: 'trav-world', category: 'travel', title: 'Travel the world', icon: '🌍', condition: { kind: 'freedomPct', target: 100 } },
  // Impact
  { key: 'imp-community', category: 'impact', title: 'Help my community', icon: '🤝', condition: { kind: 'passiveIncome', target: 30 } },
  { key: 'imp-school', category: 'impact', title: 'Fund a school', icon: '🎓', condition: { kind: 'passiveIncome', target: 150 } },
  // Skill
  { key: 'skill-focus', category: 'skill', title: 'Master of focus', icon: '🎯', condition: { kind: 'mindsetAvg', target: 70 } },
  { key: 'skill-mindset', category: 'skill', title: 'Unshakeable mindset', icon: '💎', condition: { kind: 'mindsetAvg', target: 90 } },
];

/** Human-readable "how you'll get there" line for a condition. */
export function conditionLabel(c: VisionCondition): string {
  switch (c.kind) {
    case 'netWorth':
      return `Net worth reaches ${money(c.target)}`;
    case 'passiveIncome':
      return `Passive income reaches ${money(c.target)}/mo`;
    case 'freedomPct':
      return `Freedom Meter hits ${c.target}%`;
    case 'cash':
      return `Save up ${money(c.target)} cash`;
    case 'ownAssetType':
      return `Own a real ${c.assetType}`;
    case 'mindsetAvg':
      return `Mindset average reaches ${c.target}`;
    default:
      return '';
  }
}

/** Progress (0..1) and current/target text for the numeric conditions. */
export function conditionProgress(
  c: VisionCondition,
  s: LifeState,
  comp: LifeComputed,
): { cur: number; target: number; frac: number } | null {
  switch (c.kind) {
    case 'netWorth':
      return prog(comp.netWorth, c.target);
    case 'passiveIncome':
      return prog(comp.passiveIncome, c.target);
    case 'freedomPct':
      return prog(comp.freedomPct, c.target);
    case 'cash':
      return prog(s.cash, c.target);
    case 'mindsetAvg':
      return prog(comp.mindsetAverage, c.target);
    case 'ownAssetType':
      return null; // boolean, no bar
    default:
      return null;
  }
}

function prog(cur: number, target: number) {
  return { cur, target, frac: target > 0 ? Math.min(1, Math.max(0, cur / target)) : 0 };
}
